import React, { useState, useEffect, useRef } from "react";
import { Message } from "../types";
import "./scss/Chatbot.scss";

import axios from "axios";
import { nanoid } from "nanoid";

const webhookPath =
  "https://n8n.management.live/webhook-test/fec231e4-f5cd-4f0a-9ec5-f1557f689234";
const webhookUrl =
  process.env.NODE_ENV === "development"
    ? webhookPath
    : process.env.REACT_APP_CHAT_URL || webhookPath;

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId] = useState<string>(nanoid());
  const [maxQuestions] = useState(20)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "Hi! I'm Riyasat's AI avatar. How can I help you today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const generateResponse = async (userMessage: string): Promise<string> => {
    if (messages.filter(m => m.isUser).length >= maxQuestions) {
      return "I can only answer a limited number of questions per session. Please connect with me via LinkedIn to continue the conversation.";
    }

    const lowerMessage = userMessage.toLowerCase();
    let data = {
      query: lowerMessage,
      _ses: sessionId,
    };

    const response = await axios.post(
      webhookUrl,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          "X-Webhook-Key": process.env.REACT_APP_WEBHOOK_AI_KEY,
        },
      }
    );
    if(response.status===200)
    {
      
      const responseData =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;
      return responseData.response;
    }
    return "That's an interesting question! For more detailed information about my background and experience, please feel free to explore the different sections of this portfolio and connect with me via LinkedIn.";
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(async () => {
      try {
        const response = await generateResponse(inputValue);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            content: response,
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            content: "I couldn't connect to the assistant right now. Please try again later.",
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      } finally {
        setIsTyping(false);
      }
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chatbot-container">
      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chatbot"
      >
        <i className={isOpen ? "fas fa-times" : "fas fa-comments"}></i>
      </button>

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3 style={{ color: "white" }}>I am Riyasat's AI Avatar </h3>
            <button
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chatbot"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.isUser ? "user" : "ai"}`}
              >
                <div className="message-avatar">
                  {message.isUser ? "U" : "AI"}
                </div>
                <div className="message-content">{message.content}</div>
              </div>
            ))}

            {isTyping && (
              <div className="message ai">
                <div className="message-avatar">AI</div>
                <div className="message-content typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <div className="chatbot-input-group">
              <input
                type="text"
                placeholder="Ask me anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button
                className="chatbot-send"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
              >
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
